'use client';

import { useState } from 'react';
import { useOptionalFeatureFlags } from '@/lib/providers/feature-flags-provider';
import {
  EXTERNAL_HANDLING_FEATURE_FLAG,
  useSetInventoryItemHandling
} from '@/lib/services/matter-handling';
import type { Handling } from '@/lib/services/matter-handling';
import { HandlingDialog } from './handling-dialog';

/**
 * The set/revert machinery for one inventory ITEM (EH-CR-007), the sibling of
 * {@link useInstitutionHandling} and deliberately a separate hook rather than a
 * generalisation of it.
 *
 * The two differ in the only place that matters — which endpoint they write to
 * and which subject they gate on — and folding them together would mean a runtime
 * branch on grain inside a hook whose whole job is to hide that. They share the
 * dialog, which is where the real duplication would have been.
 *
 * **Takes primitives, not an `InventoryItem`.** Its two call sites hold different
 * shapes: the drawer has the entity, while the Inventory row has only the BFF's
 * projection of it. Asking for the fields it actually reads lets both use it
 * without one of them faking the other's shape.
 *
 * **`canSetOwnHandling` is the caller's to supply, and is not a type check.** An
 * item with an institution inherits handling from it and a DB CHECK keeps the
 * item's own column null, so a control there would offer a write the database
 * refuses. The row gets the answer from the BFF and the drawer derives it from
 * the item's institution; neither restates the list of institution-less types,
 * which belongs to the constraint.
 */
/**
 * Whether this item can carry a handler of its OWN (EH-CR-007).
 *
 * An item with an institution inherits handling from it, and a DB CHECK keeps the
 * item's own column null — so a control on such an item would offer a write the
 * database refuses. Only the institution-less types can be handed over directly.
 *
 * **Named rather than inlined because two surfaces in the same drawer ask it**:
 * the header's `⋯` (`inventory-item-actions-menu`) and the handed-over panel
 * (VALE-1854). Two copies of `institution?.id ?? institutionId == null` is how one
 * of them starts offering a gesture the other hides. The list of institution-less
 * types is deliberately NOT restated here either — it belongs to the constraint,
 * and this asks the item what it has rather than what it is.
 */
export function canItemSetOwnHandling(item: {
  institution?: { id?: number | null } | null;
  institutionId?: number | null;
}): boolean {
  return (item.institution?.id ?? item.institutionId) == null;
}

export function useInventoryItemHandling({
  matterId,
  inventoryItemId,
  itemLabel,
  handling,
  canSetOwnHandling
}: {
  matterId: number;
  inventoryItemId: number;
  itemLabel: string;
  handling: Handling | null | undefined;
  canSetOwnHandling: boolean;
}) {
  const { hasFeature } = useOptionalFeatureFlags();
  const [dialogOpen, setDialogOpen] = useState(false);
  const setHandling = useSetInventoryItemHandling(matterId);

  const applies =
    hasFeature(EXTERNAL_HANDLING_FEATURE_FLAG) && canSetOwnHandling;
  const isHandled = !!handling?.handledBy;

  const takeBack = () =>
    setHandling.mutate({ inventoryItemId, handledByContactMatterId: null });

  const dialog = (
    <HandlingDialog
      matterId={matterId}
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      handling={handling}
      subjectLabel={itemLabel}
      isPending={setHandling.isPending}
      onSubmit={(data, handlerName) =>
        setHandling.mutate(
          { inventoryItemId, handlerName, ...data },
          { onSuccess: () => setDialogOpen(false) }
        )
      }
    />
  );

  return {
    applies,
    isHandled,
    isPending: setHandling.isPending,
    openDialog: () => setDialogOpen(true),
    takeBack,
    dialog
  };
}
